import { LOAD_AAFEATURE_FAILURE, LOAD_AAFEATURE_REQUEST, LOAD_AAFEATURE_SUCCESS } from "../action-types.js";
import { type ReduxAction, createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type AaFeature } from "@/graphql.ts";

/**
 * State definition
 */
export interface IAaFeatureState {
  aaFeature?: AaFeature;
  isLoading: boolean;
}

const initialState: IAaFeatureState = {
  aaFeature: undefined,
  isLoading: true,
};

/**
 * Reducers
 */
const aaFeatureReducer = createReducer(initialState, {
  [LOAD_AAFEATURE_REQUEST]: (state: IAaFeatureState): IAaFeatureState => {
    return { ...state, isLoading: true };
  },
  [LOAD_AAFEATURE_SUCCESS]: (state: IAaFeatureState, payload: AaFeature): IAaFeatureState => {
    return { ...state, isLoading: false, aaFeature: payload };
  },
  [LOAD_AAFEATURE_FAILURE]: (state: IAaFeatureState): IAaFeatureState => {
    return { ...state, isLoading: false, aaFeature: undefined };
  },
});

/**
 * Action creators
 */

// Last request in time unique identifier
let lastFetchedAaFeatureId = -1;

function loadAaFeature(aaFeatureId: number): ReduxAction<Promise<IAaFeatureState>> {
  return (dispatch, getState, webApiClient) => {
    const state = getState();
    lastFetchedAaFeatureId = aaFeatureId;

    // fetch the gene only if no loading is currently in progress and gene is either absent or doesn't match the gene ID
    if (state.aaFeature.aaFeature?.id !== aaFeatureId) {
      dispatch({ type: LOAD_AAFEATURE_REQUEST });

      return webApiClient
        .fetchAaFeature(aaFeatureId)
        .then((aaFeature) => {
          if (lastFetchedAaFeatureId === aaFeature.id) {
            dispatch({
              type: LOAD_AAFEATURE_SUCCESS,
              payload: aaFeature,
            });
            return aaFeature;
          }

          return state.aaFeature.aaFeature;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_AAFEATURE_FAILURE,
            alertTitle: "Fetching of aaFeatures Failed",
          }),
        );
    } else {
      // We reset the loading flag to not stay on a loading state indefinitely
      // because lastFetchId can be not synchronized from state.gene.gene.id
      dispatch({
        type: LOAD_AAFEATURE_SUCCESS,
        payload: state.aaFeature.aaFeature,
      });

      return Promise.resolve(state.aaFeature.aaFeature);
    }
  };
}

export { aaFeatureReducer, loadAaFeature };
